import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { MobileNavigation } from '../components/MobileNavigation';
import { QrCode, User, ArrowLeft, Basketball } from '@phosphor-icons/react';
import { getApiBaseUrl } from '../utils/api';
import { toast } from 'sonner';

export function MyQRCodes() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);

  useEffect(() => {
    loadPlayers();
  }, []);

  const loadPlayers = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/players`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
        // Auto-select first player if only one
        if (data.length === 1) {
          loadQRCode(data[0]);
        }
      }
    } catch (error) {
      console.error('Load players error:', error);
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  };

  const loadQRCode = async (player) => {
    setSelectedPlayer(player);
    setLoadingQR(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/players/${player.player_id}/qr`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setQrCode(data.qr_code);
      } else {
        toast.error('Failed to load QR code');
      }
    } catch (error) {
      console.error('Load QR error:', error);
      toast.error('Failed to load QR code');
    } finally {
      setLoadingQR(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  // If showing QR code for a player
  if (selectedPlayer && qrCode) {
    return (
      <div className="min-h-screen bg-background pb-24" data-testid="my-qr-page">
        {/* Header */}
        <div className="glass-effect sticky top-0 z-40">
          <div className="px-4 py-4">
            <div className="flex items-center gap-3">
              {players.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedPlayer(null);
                    setQrCode(null);
                  }}
                  className="p-2"
                >
                  <ArrowLeft size={20} />
                </Button>
              )}
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Check-In Code</p>
                <h1 className="text-xl font-black uppercase tracking-tight">{selectedPlayer.name}</h1>
              </div>
            </div>
          </div>
        </div>

        {/* QR Code Display */}
        <div className="p-4">
          <Card className="bg-white rounded-2xl overflow-hidden">
            <CardContent className="p-6 flex flex-col items-center">
              {loadingQR ? (
                <div className="w-64 h-64 flex items-center justify-center">
                  <LoadingSpinner size="xl" />
                </div>
              ) : (
                <>
                  <img 
                    src={qrCode} 
                    alt={`QR Code for ${selectedPlayer.name}`}
                    className="w-64 h-64 object-contain"
                  />
                  <div className="mt-4 text-center">
                    <h2 className="text-xl font-black text-black uppercase">{selectedPlayer.name}</h2>
                    <p className="text-gray-600 text-sm">
                      Grade {selectedPlayer.grade} • Age {selectedPlayer.age}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <div className="mt-6 text-center space-y-3">
            <div className="flex items-center justify-center gap-2 text-primary">
              <Basketball size={24} weight="duotone" />
              <span className="font-bold uppercase tracking-wide">STAY ON Hoops</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Show this QR code at check-in for quick entry
            </p>
            <p className="text-xs text-muted-foreground/70">
              Staff will scan this code to verify your registration
            </p>
          </div>
        </div>

        <MobileNavigation userRole="parent" />
      </div>
    );
  }

  // Player selection view
  return (
    <div className="min-h-screen bg-background pb-24" data-testid="my-qr-page">
      {/* Header */}
      <div className="glass-effect sticky top-0 z-40">
        <div className="px-6 py-5">
          <div className="flex items-center gap-3">
            <QrCode size={28} weight="duotone" className="text-primary" />
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Session Check-In</p>
              <h1 className="text-2xl font-black uppercase tracking-tight">QR Codes</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {players.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <User size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-lg mb-2">NO PLAYERS YET</h3>
              <p className="text-muted-foreground mb-4">
                Add players to see their check-in QR codes
              </p>
              <Button 
                onClick={() => window.location.href = '/players'}
                className="btn-primary"
              >
                Add Players
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              Tap a player to show their check-in QR code
            </p>
            
            {players.map((player) => (
              <Card 
                key={player.player_id}
                className="bg-card border-border cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => loadQRCode(player)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <QrCode size={24} weight="duotone" className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-bold">{player.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          Grade {player.grade} • Age {player.age}
                        </p>
                      </div>
                    </div>
                    <span className="text-primary font-medium text-sm">Show QR →</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </div>

      <MobileNavigation userRole="parent" />
    </div>
  );
}

export default MyQRCodes;
